import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageMetadata } from '../hooks/usePageMetadata';
import {
    Search,
    Bookmark,
    Clock,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    Eye,
    MessageCircle,
    Tag,
    TrendingUp,
    Sparkles,
    Heart,
    BookOpen,
    Target,
    Bookmark as BookmarkIcon,
    User
} from 'lucide-react';

import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';
import { useGetPublicBlogsQuery, useGetFeaturedBlogsQuery } from '../store/slices/apiSlice';

const Blogs = () => {
    const [searchTerm, setSearchTerm] = useState('');

    // Set page metadata
    usePageMetadata('blogs');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [isVisible, setIsVisible] = useState(false);
    const [sortBy, setSortBy] = useState('latest');
    const [postsPerPage, setPostsPerPage] = useState(5);

    // Fetch featured blogs from API
    const { data: featuredBlogsData, isLoading: featuredLoading, error: featuredError } = useGetFeaturedBlogsQuery();

    // Fetch all blogs from API
    const { data: blogsData, isLoading: blogsLoading, error: blogsError } = useGetPublicBlogsQuery();

    const isLoading = featuredLoading || blogsLoading;
    const error = featuredError || blogsError;

    useEffect(() => {
        setIsVisible(true);
    }, []);

    // Scroll to top when page changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage]);

    // Reset to page 1 when search, category, or posts per page changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedCategory, postsPerPage]);

    // Get featured blogs from API
    const featuredBlogs = featuredBlogsData?.data || [];

    // Get all blogs from API
    const allBlogs = blogsData?.data?.blogs || [];

    // Transform featured blog data to match component structure
    const transformedFeaturedBlogs = Array.isArray(featuredBlogs) ? featuredBlogs.map((blog: any) => ({
        id: blog.id,
        slug: blog.slug,
        title: blog.title,
        excerpt: blog.desc,
        author: {
            name: blog.author,
            role: "Author",
            image: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80"
        },
        category: blog.category,
        readTime: `${blog.readTime} min read`,
        publishDate: blog.publishedAt,
        image: blog.image || "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=80",
        views: blog.views,
        likes: blog.likes,
        comments: blog.comments,
        featured: blog.featured,
        tags: blog.tags || []
    })) : [];

    // Transform all blog data to match component structure
    const transformedAllBlogs = Array.isArray(allBlogs) ? allBlogs.map((blog: any) => ({
        id: blog.id,
        slug: blog.slug,
        title: blog.title,
        excerpt: blog.desc,
        author: {
            name: blog.author,
            role: "Author",
            image: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80"
        },
        category: blog.category,
        readTime: `${blog.readTime} min read`,
        publishDate: blog.publishedAt,
        image: blog.image || "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=80",
        views: blog.views,
        likes: blog.likes,
        comments: blog.comments,
        featured: blog.featured,
        tags: blog.tags || []
    })) : [];

    // Use featured blogs for featured posts and all blogs for regular posts
    const featuredPosts = transformedFeaturedBlogs;
    const blogPosts = transformedAllBlogs;

    // Dynamic categories based on actual blog data
    const allCategories = [...new Set(transformedAllBlogs.map(blog => blog.category))];
    const categories = [
        { id: 'all', name: 'All Posts', count: transformedAllBlogs.length, color: 'from-blue-500 to-purple-600' },
        ...allCategories.map(category => ({
            id: category,
            name: category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' '),
            count: transformedAllBlogs.filter(blog => blog.category === category).length,
            color: 'from-green-500 to-emerald-600'
        }))
    ];

    // Dynamic popular tags from actual blog data
    const allTags = [...new Set(transformedAllBlogs.flatMap(blog => blog.tags || []))];
    const popularTags = allTags.length > 0 ? allTags : [
        "Investment", "Market Analysis", "Technology", "Success Stories", "Risk Management",
        "Portfolio", "Diversification", "AI", "Blockchain", "Startups", "ROI", "Strategy"
    ];

    // Filtered posts based on search and category
    const filteredPosts = blogPosts.filter(post => {
        const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    // Pagination
    const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
    const currentPosts = filteredPosts.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-white">
                <Navbar />
                <div className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-xl text-gray-600">Loading content...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-white">
                <Navbar />
                <div className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Failed to Load Content</h2>
                        <p className="text-gray-600 mb-4">There was an error loading the blog posts.</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <Navbar />

            {/* Simple Hero Section */}
            <section className="relative py-12 px-6 bg-white border-b border-gray-200">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center">
                        <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full text-sm font-medium mb-4 text-gray-600 border border-gray-200">
                            <BookOpen className="w-4 h-4" />
                            Blog & Insights
                        </div>

                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Latest Articles
                        </h1>

                        <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                            Stay updated with expert insights and market analysis
                        </p>

                        {/* Simple Search Bar */}
                        <div className="max-w-2xl mx-auto">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search articles..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all duration-300"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Simple Categories & Filters */}
            <section className="py-6 px-6 bg-gray-50 border-b border-gray-200">
                <div className="container mx-auto max-w-7xl">
                    <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                        {/* Categories */}
                        <div className="flex flex-wrap gap-2">
                            {categories.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${selectedCategory === category.id
                                        ? 'bg-gray-700 text-white shadow-md'
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                        }`}
                                >
                                    <span className="flex items-center gap-2">
                                        {category.name}
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${selectedCategory === category.id ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'}`}>
                                            {category.count}
                                        </span>
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Sort Options */}
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="relative">
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 transition-all duration-200 appearance-none pr-10"
                                >
                                    <option value="latest">Latest Articles</option>
                                    <option value="popular">Most Popular</option>
                                    <option value="trending">Trending Now</option>
                                </select>
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                </div>
                            </div>

                            <div className="relative">
                                <select
                                    value={postsPerPage}
                                    onChange={(e) => {
                                        setPostsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 transition-all duration-200 appearance-none pr-10"
                                >
                                    <option value="3">3 per page</option>
                                    <option value="5">5 per page</option>
                                    <option value="6">6 per page</option>
                                    <option value="9">9 per page</option>
                                    <option value="12">12 per page</option>
                                    <option value="24">24 per page</option>
                                </select>
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            {/* Main Blog Posts */}
            <section className="py-12 px-6 bg-gray-50">
                <div className="container mx-auto max-w-7xl">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Main Content */}
                        <div className="lg:col-span-2">
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                                        All Articles
                                    </h2>
                                    <p className="text-gray-600 text-sm">{filteredPosts.length} of {blogPosts.length} articles</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {currentPosts.map((post, index) => (
                                    <Link key={post.id} to={`/blog/${post.slug || post.id}`} className="group bg-white rounded-xl shadow hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-200 hover:border-blue-300">
                                        <div className="flex flex-col md:flex-row md:items-stretch">
                                            <div className="md:w-2/5 relative bg-gray-100 aspect-video md:aspect-auto md:h-auto min-h-[280px] md:min-h-full flex items-center justify-center overflow-hidden">
                                                <img
                                                    src={post.image}
                                                    alt={post.title}
                                                    className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-300"
                                                />
                                                <div className="absolute bottom-3 left-3 bg-white text-gray-800 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 shadow">
                                                    <Clock className="w-3 h-3" />
                                                    {post.readTime}
                                                </div>
                                            </div>
                                            <div className="md:w-3/5 p-6">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-md text-xs font-medium border border-gray-200">
                                                        {categories.find(c => c.id === post.category)?.name}
                                                    </span>
                                                    <span className="text-gray-500 text-xs">• {formatDateUtil(post.publishDate)}</span>
                                                </div>
                                                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200 leading-tight">
                                                    {post.title}
                                                </h3>
                                                <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                                                    {post.excerpt}
                                                </p>
                                                <div className="flex items-center justify-between text-xs text-gray-500">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                                            <User className="w-4 h-4 text-gray-600" />
                                                        </div>
                                                        <span className="font-medium text-gray-700">{post.author.name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="flex items-center gap-1">
                                                            <Eye className="w-3 h-3" />
                                                            {post.views}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Heart className="w-3 h-3" />
                                                            {post.likes}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <MessageCircle className="w-3 h-3" />
                                                            {post.comments}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>

                            {/* Premium Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-16 space-y-6">
                                    {/* Pagination Info */}
                                    <div className="text-center">
                                        <p className="text-sm text-gray-600">
                                            Showing <span className="font-semibold text-blue-600">{(currentPage - 1) * postsPerPage + 1}</span> to{' '}
                                            <span className="font-semibold text-blue-600">{Math.min(currentPage * postsPerPage, filteredPosts.length)}</span> of{' '}
                                            <span className="font-semibold text-blue-600">{filteredPosts.length}</span> articles
                                        </p>
                                    </div>

                                    {/* Pagination Buttons */}
                                    <div className="flex items-center justify-center gap-2 flex-wrap">
                                        <button
                                            onClick={() => setCurrentPage(1)}
                                            disabled={currentPage === 1}
                                            className="px-4 h-12 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-lg text-sm font-medium"
                                        >
                                            First
                                        </button>

                                        <button
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                            className="w-12 h-12 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-lg"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>

                                        {/* Page Numbers with Ellipsis */}
                                        {(() => {
                                            const pages = [];
                                            const maxVisiblePages = 5;

                                            if (totalPages <= maxVisiblePages + 2) {
                                                // Show all pages if total is small
                                                for (let i = 1; i <= totalPages; i++) {
                                                    pages.push(i);
                                                }
                                            } else {
                                                // Show first page
                                                pages.push(1);

                                                // Calculate range around current page
                                                let startPage = Math.max(2, currentPage - 1);
                                                let endPage = Math.min(totalPages - 1, currentPage + 1);

                                                // Adjust if at the beginning
                                                if (currentPage <= 3) {
                                                    endPage = Math.min(totalPages - 1, 4);
                                                }

                                                // Adjust if at the end
                                                if (currentPage >= totalPages - 2) {
                                                    startPage = Math.max(2, totalPages - 3);
                                                }

                                                // Add ellipsis if needed
                                                if (startPage > 2) {
                                                    pages.push('...');
                                                }

                                                // Add middle pages
                                                for (let i = startPage; i <= endPage; i++) {
                                                    pages.push(i);
                                                }

                                                // Add ellipsis if needed
                                                if (endPage < totalPages - 1) {
                                                    pages.push('...');
                                                }

                                                // Show last page
                                                pages.push(totalPages);
                                            }

                                            return pages.map((page, index) => {
                                                if (page === '...') {
                                                    return (
                                                        <span key={`ellipsis-${index}`} className="w-12 h-12 flex items-center justify-center text-gray-400">
                                                            ...
                                                        </span>
                                                    );
                                                }

                                                return (
                                                    <button
                                                        key={page}
                                                        onClick={() => setCurrentPage(page as number)}
                                                        className={`w-12 h-12 rounded-lg border flex items-center justify-center transition-all duration-200 font-semibold ${currentPage === page
                                                            ? 'bg-blue-600 text-white border-blue-600 shadow'
                                                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {page}
                                                    </button>
                                                );
                                            });
                                        })()}

                                        <button
                                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                            disabled={currentPage === totalPages}
                                            className="w-12 h-12 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-lg"
                                        >
                                            <ChevronRight className="w-5 h-5" />
                                        </button>

                                        <button
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={currentPage === totalPages}
                                            className="px-4 h-12 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-lg text-sm font-medium"
                                        >
                                            Last
                                        </button>
                                    </div>

                                    {/* Quick Jump */}
                                    <div className="flex items-center justify-center gap-3">
                                        <span className="text-sm text-gray-600">Jump to page:</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max={totalPages}
                                            value={currentPage}
                                            onChange={(e) => {
                                                const page = parseInt(e.target.value);
                                                if (page >= 1 && page <= totalPages) {
                                                    setCurrentPage(page);
                                                }
                                            }}
                                            className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-center focus:outline-none focus:border-blue-500 focus:shadow-lg transition-all duration-300"
                                        />
                                        <span className="text-sm text-gray-600">of {totalPages}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Popular Tags */}
                            <div className="bg-white rounded-xl p-6 shadow border border-gray-200">
                                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Tag className="w-5 h-5 text-blue-600" />
                                    Popular Tags
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {popularTags.map((tag, index) => (
                                        <button
                                            key={index}
                                            className="px-3 py-1.5 bg-gray-50 text-gray-700 rounded-md text-sm font-medium hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 border border-gray-200"
                                        >
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>


                            {/* Trending Articles */}
                            <div className="bg-white rounded-xl p-6 shadow border border-gray-200">
                                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-green-600" />
                                    Trending Articles
                                </h3>
                                <div className="space-y-4">
                                    {blogPosts.slice(0, 3).map((post, index) => (
                                        <Link key={post.id} to={`/blog/${post.slug || post.id}`} className="flex gap-3 group cursor-pointer">
                                            <div className="relative">
                                                <img
                                                    src={post.image}
                                                    alt={post.title}
                                                    className="w-16 h-16 rounded-lg object-cover bg-gray-100 flex-shrink-0 group-hover:scale-105 transition-transform duration-200"
                                                />
                                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                                    {index + 1}
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900 text-sm group-hover:text-blue-600 transition-colors duration-200 line-clamp-2 leading-tight">
                                                    {post.title}
                                                </h4>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {formatDateUtil(post.publishDate)}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                                    <span className="flex items-center gap-1">
                                                        <Eye className="w-3 h-3" />
                                                        {post.views}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Heart className="w-3 h-3" />
                                                        {post.likes}
                                                    </span>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            {/* FAQ Section */}
            <FAQ
                title="Blog & Content Questions"
                subtitle="Everything you need to know about our investment insights and educational content"
                faqs={[
                    {
                        id: 1,
                        question: "How often do you publish new blog posts?",
                        answer: "We publish new blog posts regularly, typically 2-3 times per week. Our content includes market analysis, investment insights, success stories, and educational articles."
                    },
                    {
                        id: 2,
                        question: "What topics do your blog posts cover?",
                        answer: "Our blog covers a wide range of investment-related topics including market trends, real estate insights, technology investments, portfolio management, risk assessment, success stories, and educational content for both beginners and experienced investors."
                    },
                    {
                        id: 3,
                        question: "Can I share blog posts on social media?",
                        answer: "Yes, we encourage sharing our content! Each blog post includes social sharing buttons, and you can easily share articles on Facebook, Twitter, LinkedIn, and other platforms. We believe in spreading valuable investment knowledge to help more people make informed decisions."
                    },
                    {
                        id: 4,
                        question: "Are the blog posts written by investment experts?",
                        answer: "Absolutely! Our blog content is written by experienced investment professionals, financial analysts, and industry experts. Each article is thoroughly researched and reviewed to ensure accuracy and provide valuable insights to our readers."
                    },
                    {
                        id: 5,
                        question: "Can I bookmark or save blog posts for later reading?",
                        answer: "Yes, you can bookmark any blog post for later reading. Simply click the bookmark icon on any article, and it will be saved to your personal reading list. You can access your bookmarked posts anytime from your dashboard."
                    },
                    {
                        id: 6,
                        question: "How can I stay updated with new content?",
                        answer: "You can stay updated by visiting our blog regularly, following us on social media, or checking our website for the latest investment insights and market updates."
                    }
                ]}
            />

            <Footer />
        </div>
    );
};

export default Blogs;
